/**
 * 날짜/시간 유틸리티 함수 (KST 기준)
 * 프로그램 상태 계산 시 사용
 */

/**
 * 현재 KST 시간을 ISO 문자열로 반환
 * @returns 현재 KST 시간 (예: "2025-01-15T14:30:00")
 */
export const getKSTNow = (): Date => {
  const now = new Date();
  // UTC에 9시간 더해서 KST로 변환
  return new Date(now.getTime() + 9 * 60 * 60 * 1000);
};

/**
 * 현재 KST 날짜를 YYYY-MM-DD 형식으로 반환
 */
export const getKSTDateString = (): string => {
  const kst = getKSTNow();
  return kst.toISOString().split('T')[0];
};

/**
 * 현재 KST 시간을 HH:MM 형식으로 반환
 */
export const getKSTTimeString = (): string => {
  const kst = getKSTNow();
  return kst.toISOString().split('T')[1].substring(0, 5);
};

/**
 * 날짜+시간 문자열을 KST Date 객체로 변환
 * @param dateStr 날짜 문자열 (YYYY-MM-DD)
 * @param timeStr 시간 문자열 (HH:MM 또는 HH:MM:SS), 없으면 기본값 사용
 * @param defaultTime 시간이 없을 때 기본값 ('00:00:00' 또는 '23:59:59')
 */
export const parseKSTDateTime = (
  dateStr: string,
  timeStr?: string,
  defaultTime: string = '00:00:00'
): Date => {
  const time = timeStr || defaultTime;
  // +09:00을 붙여서 KST로 파싱
  return new Date(`${dateStr}T${time.length === 5 ? time + ':00' : time}+09:00`);
};

interface CourseForStatus {
  registrationStartDate?: string;  // nullable
  registrationEndDate?: string;    // nullable
  registrationStartTime?: string;
  registrationEndTime?: string;
  courseStartDate?: string;        // nullable
  courseEndDate?: string;          // nullable
}

/**
 * 프로그램/강좌의 실제 상태를 KST 기준으로 계산
 * @param course 강좌 정보 (날짜/시간 필드 포함)
 * @returns 'upcoming' | 'recruiting' | 'ongoing' | 'completed'
 */
export const getActualCourseStatus = (course: CourseForStatus): 'upcoming' | 'recruiting' | 'ongoing' | 'completed' => {
  // 날짜 필드가 없으면 기본값 'upcoming' 반환
  if (!course.registrationStartDate || !course.registrationEndDate ||
      !course.courseStartDate || !course.courseEndDate) {
    return 'upcoming';
  }

  const now = new Date(); // 현재 시간 (로컬)

  // KST 기준으로 각 시점 계산
  const regStart = parseKSTDateTime(
    course.registrationStartDate,
    course.registrationStartTime,
    '00:00:00'
  );
  const regEnd = parseKSTDateTime(
    course.registrationEndDate,
    course.registrationEndTime,
    '23:59:59'
  );
  const courseStart = parseKSTDateTime(course.courseStartDate, undefined, '00:00:00');
  const courseEnd = parseKSTDateTime(course.courseEndDate, undefined, '23:59:59');

  // 상태 판단 (우선순위 순서)
  // 1. 교육 종료일 지남 → 종료
  if (now > courseEnd) {
    return 'completed';
  }

  // 2. 교육 기간 중 → 진행중
  if (now >= courseStart && now <= courseEnd) {
    return 'ongoing';
  }

  // 3. 신청 기간 중 → 모집중
  if (now >= regStart && now <= regEnd) {
    return 'recruiting';
  }

  // 4. 신청 시작 전 → 모집예정
  if (now < regStart) {
    return 'upcoming';
  }

  // 5. 신청 마감 후 ~ 교육 시작 전 → 모집예정 (또는 마감으로 볼 수도 있음)
  // 현재 로직에서는 upcoming으로 처리
  return 'upcoming';
};

/**
 * ProgramClass 타입용 상태 계산
 */
interface ProgramClassForStatus {
  registration_start_date: string;
  registration_end_date: string;
  registration_start_time?: string;
  registration_end_time?: string;
  course_start_date: string;
  course_end_date: string;
}

export const getActualClassStatus = (programClass: ProgramClassForStatus): 'upcoming' | 'recruiting' | 'ongoing' | 'completed' => {
  return getActualCourseStatus({
    registrationStartDate: programClass.registration_start_date,
    registrationEndDate: programClass.registration_end_date,
    registrationStartTime: programClass.registration_start_time,
    registrationEndTime: programClass.registration_end_time,
    courseStartDate: programClass.course_start_date,
    courseEndDate: programClass.course_end_date,
  });
};

/**
 * 강좌의 모든 반(class)을 고려한 실제 상태 계산
 * - 하나의 반이라도 모집 중이면 '모집중' 반환
 * - 모든 반이 진행 중이면 '진행중'
 * - 모든 반이 종료되면 '종료'
 * - 그 외에는 '모집예정'
 */
export const getCourseStatusWithClasses = (
  course: CourseForStatus,
  classes: ProgramClassForStatus[]
): 'upcoming' | 'recruiting' | 'ongoing' | 'completed' => {
  // 반이 없으면 기존 로직 사용
  if (!classes || classes.length === 0) {
    return getActualCourseStatus(course);
  }

  const now = new Date();

  // 각 반의 상태 확인
  let hasRecruiting = false;
  let hasOngoing = false;
  let hasUpcoming = false;
  let allCompleted = true;

  for (const programClass of classes) {
    const regStart = parseKSTDateTime(
      programClass.registration_start_date,
      programClass.registration_start_time,
      '00:00:00'
    );
    const regEnd = parseKSTDateTime(
      programClass.registration_end_date,
      programClass.registration_end_time,
      '23:59:59'
    );
    const courseStart = parseKSTDateTime(programClass.course_start_date, undefined, '00:00:00');
    const courseEnd = parseKSTDateTime(programClass.course_end_date, undefined, '23:59:59');

    // 반 상태 판단
    if (now > courseEnd) {
      // 이 반은 종료됨
      continue;
    }

    allCompleted = false;

    if (now >= courseStart && now <= courseEnd) {
      hasOngoing = true;
    } else if (now >= regStart && now <= regEnd) {
      hasRecruiting = true;
    } else if (now < regStart) {
      hasUpcoming = true;
    }
  }

  // 우선순위: recruiting > ongoing > upcoming > completed
  // 하나라도 모집 중이면 모집중
  if (hasRecruiting) {
    return 'recruiting';
  }
  // 모든 반이 종료됨
  if (allCompleted) {
    return 'completed';
  }
  // 진행 중인 반이 있음
  if (hasOngoing) {
    return 'ongoing';
  }
  // 모집 예정인 반이 있음
  if (hasUpcoming) {
    return 'upcoming';
  }
  // 모집 마감 후 교육 시작 전 (어떤 반도 recruiting/ongoing/upcoming/completed가 아닌 경우)
  // 이 경우는 없어야 하지만, 안전을 위해 ongoing 반환 (모집 마감 상태로 간주)
  return 'ongoing';
};
